/**
 * Club Logo Service
 * Handles club logo image uploads and URL generation for Supabase Storage.
 * Club logos are circular/square (1:1), max 400px — distinct from club banners (3:1, 1200px).
 */

import { supabase } from '../utils/supabase';

const CLUB_LOGOS_BUCKET = 'club-logos';
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGE_DIMENSION = 4000;
const MIN_LOGO_SIZE = 100;

const MAGIC_NUMBERS = {
  jpeg: [0xFF, 0xD8, 0xFF],
  png: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A],
  webp: [0x52, 0x49, 0x46, 0x46]
};

/**
 * Get the public URL for a club logo image
 * @param {string|null} path - Storage path (e.g., "{clubId}.jpg")
 * @param {boolean} bustCache - Add timestamp to bust browser cache (default: true)
 * @returns {string|null}
 */
export function getClubLogoPublicUrl(path, bustCache = true) {
  if (!path || path.trim() === '') return null;

  try {
    const { data } = supabase.storage.from(CLUB_LOGOS_BUCKET).getPublicUrl(path);
    if (!data?.publicUrl) return null;

    if (bustCache) {
      const separator = data.publicUrl.includes('?') ? '&' : '?';
      return `${data.publicUrl}${separator}t=${Date.now()}`;
    }
    return data.publicUrl;
  } catch (error) {
    console.error('Error getting club logo public URL:', error);
    return null;
  }
}

async function verifyFileMagicNumbers(file) {
  try {
    const buffer = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    if (bytes[0] === MAGIC_NUMBERS.jpeg[0] && bytes[1] === MAGIC_NUMBERS.jpeg[1] && bytes[2] === MAGIC_NUMBERS.jpeg[2]) {
      return { valid: true, detectedType: 'image/jpeg' };
    }

    if (
      bytes[0] === MAGIC_NUMBERS.png[0] && bytes[1] === MAGIC_NUMBERS.png[1] &&
      bytes[2] === MAGIC_NUMBERS.png[2] && bytes[3] === MAGIC_NUMBERS.png[3] &&
      bytes[4] === MAGIC_NUMBERS.png[4] && bytes[5] === MAGIC_NUMBERS.png[5] &&
      bytes[6] === MAGIC_NUMBERS.png[6] && bytes[7] === MAGIC_NUMBERS.png[7]
    ) {
      return { valid: true, detectedType: 'image/png' };
    }

    if (
      bytes[0] === MAGIC_NUMBERS.webp[0] && bytes[1] === MAGIC_NUMBERS.webp[1] &&
      bytes[2] === MAGIC_NUMBERS.webp[2] && bytes[3] === MAGIC_NUMBERS.webp[3] &&
      bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
    ) {
      return { valid: true, detectedType: 'image/webp' };
    }

    return { valid: false, error: 'File does not match any allowed image format. The file may be corrupted or not a real image.' };
  } catch (error) {
    console.error('Error verifying file magic numbers:', error);
    return { valid: false, error: 'Failed to verify file format' };
  }
}

async function validateImageDimensions(file) {
  try {
    const imageBitmap = await createImageBitmap(file);

    if (imageBitmap.width > MAX_IMAGE_DIMENSION || imageBitmap.height > MAX_IMAGE_DIMENSION) {
      imageBitmap.close();
      return { valid: false, error: `Image dimensions exceed maximum allowed (${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION})` };
    }

    if (imageBitmap.width < MIN_LOGO_SIZE || imageBitmap.height < MIN_LOGO_SIZE) {
      imageBitmap.close();
      return { valid: false, error: `Image is too small for a logo (minimum ${MIN_LOGO_SIZE}x${MIN_LOGO_SIZE} pixels)` };
    }

    const dimensions = { width: imageBitmap.width, height: imageBitmap.height };
    imageBitmap.close();
    return { valid: true, ...dimensions };
  } catch (error) {
    console.error('Error validating image dimensions:', error);
    return { valid: false, error: 'Failed to load image. The file may be corrupted or not a valid image.' };
  }
}

function validateLogoFile(file) {
  if (!file) return { valid: false, error: 'No file provided' };

  if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    return { valid: false, error: `Invalid file type. Allowed types: JPG, PNG, WebP` };
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit` };
  }

  if (file.size < 1000) {
    return { valid: false, error: 'File is too small to be a valid image' };
  }

  return { valid: true };
}

async function resizeImage(file, maxDimension = 400, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create blob from canvas'));
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a club logo image to Supabase Storage
 * @param {string} clubId - Club UUID
 * @param {File} file - Image file to upload (should already be cropped to 1:1 by the caller)
 * @param {Object} options
 * @param {boolean} options.resize - Whether to resize the image (default: true)
 * @param {number} options.maxDimension - Max dimension for resize (default: 400)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.9)
 * @returns {Promise<{ success: boolean, path?: string, publicUrl?: string, error?: string }>}
 */
export async function uploadClubLogo(
  clubId,
  file,
  options = { resize: true, maxDimension: 400, quality: 0.9 }
) {
  try {
    const validation = validateLogoFile(file);
    if (!validation.valid) return { success: false, error: validation.error };

    const magicNumberCheck = await verifyFileMagicNumbers(file);
    if (!magicNumberCheck.valid) return { success: false, error: magicNumberCheck.error };

    const dimensionCheck = await validateImageDimensions(file);
    if (!dimensionCheck.valid) return { success: false, error: dimensionCheck.error };

    const fileExt = magicNumberCheck.detectedType.split('/')[1];
    const storagePath = `${clubId}.${fileExt === 'jpeg' ? 'jpg' : fileExt}`;

    let uploadFile = file;
    if (options.resize !== false) {
      try {
        const resizedBlob = await resizeImage(file, options.maxDimension || 400, options.quality || 0.9);
        uploadFile = new File([resizedBlob], file.name, { type: 'image/jpeg' });
      } catch (resizeError) {
        console.warn('Logo resize failed, uploading original:', resizeError);
      }
    }

    const { error: uploadError } = await supabase
      .storage
      .from(CLUB_LOGOS_BUCKET)
      .upload(storagePath, uploadFile, {
        cacheControl: '300',
        upsert: true
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      return { success: false, error: uploadError.message };
    }

    const { error: updateError } = await supabase
      .from('clubs')
      .update({ logo_image_url: storagePath })
      .eq('id', clubId);

    if (updateError) {
      console.error('Database update error:', updateError);
      return { success: false, error: `File uploaded but failed to update database: ${updateError.message}` };
    }

    const publicUrl = getClubLogoPublicUrl(storagePath);
    return { success: true, path: storagePath, publicUrl };

  } catch (error) {
    console.error('Upload club logo error:', error);
    return { success: false, error: error.message || 'Unknown error during upload' };
  }
}

/**
 * Remove a club logo
 * @param {string} clubId - Club UUID
 * @param {string} path - Current logo storage path
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function removeClubLogo(clubId, path) {
  try {
    if (path && path.trim() !== '') {
      const { error: deleteError } = await supabase
        .storage
        .from(CLUB_LOGOS_BUCKET)
        .remove([path]);

      if (deleteError) {
        console.warn('Storage delete warning:', deleteError);
      }
    }

    const { error: updateError } = await supabase
      .from('clubs')
      .update({ logo_image_url: null })
      .eq('id', clubId);

    if (updateError) {
      console.error('Database update error:', updateError);
      return { success: false, error: `Failed to update database: ${updateError.message}` };
    }

    return { success: true };

  } catch (error) {
    console.error('Remove club logo error:', error);
    return { success: false, error: error.message || 'Unknown error during removal' };
  }
}
